import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Stack
} from '@mui/material';

import {
  CloseOutlined,
  VisibilityOffOutlined,
  VisibilityOutlined
} from '@mui/icons-material';

import {
  cancelButtonSx,
  changePasswordSubmitSx,
  dialogActionsSx,
  dialogCloseButtonSx,
  dialogContentSx,
  dialogFormSx,
  dialogHeaderSx,
  dialogSubtitleSx,
  dialogTitleSx,
  dialogTitleTextSx,
  passwordDialogPaperSx,
  visibilityButtonSx
} from './styles';

import {
  dialogHeaderStyles,
  textFieldSx,
  menuProps
} from '../../../Common/components/Styles/InputStyles';
import CommonDialogActions from '../../../Common/components/CommonDialogActions';

const initialForm = {
  confirmPassword: '',
  newPassword: ''
};

const ChangePasswordModal = ({ onClose, onSubmit, open }) => {
  const [form, setForm] = useState(initialForm);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  useEffect(() => {
    if (!open) {
      setForm(initialForm);
      setShowConfirmPassword(false);
      setShowNewPassword(false);
    }
  }, [open]);

  const handleChange = field => event => {
    setForm(previousForm => ({
      ...previousForm,
      [field]: event.target.value
    }));
  };

  const handleSubmit = () => {
    onSubmit(form);
  };

  const passwordsMatch = form.newPassword === form.confirmPassword;

  const isSubmitDisabled =
    !form.newPassword || !form.confirmPassword || !passwordsMatch;

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      onClose={onClose}
      open={open}
      PaperProps={{
        sx: passwordDialogPaperSx
      }}>
      <DialogTitle sx={dialogHeaderStyles.dialogheader}>
        <Box>
          <Typography sx={dialogHeaderStyles.title}>Change Password</Typography>
          <Typography sx={dialogHeaderStyles.subtitle}>
            Update your password to keep your account secure
          </Typography>
        </Box>

        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={dialogHeaderStyles.dialogheaderclose}>
          <CloseOutlined sx={{ fontSize: 22 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2 }}>
        <Stack spacing={1.6} sx={{ pt: 2 }}>
          <TextField
            error={Boolean(form.confirmPassword && !passwordsMatch)}
            fullWidth
            label="New Password"
            onChange={handleChange('newPassword')}
            required
            size="small"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        showNewPassword
                          ? 'Hide new password'
                          : 'Show new password'
                      }
                      edge="end"
                      onClick={() => setShowNewPassword(previous => !previous)}
                      sx={visibilityButtonSx}>
                      {showNewPassword ? (
                        <VisibilityOffOutlined sx={{ fontSize: 18 }} />
                      ) : (
                        <VisibilityOutlined sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                )
              }
            }}
            sx={textFieldSx}
            type={showNewPassword ? 'text' : 'password'}
            value={form.newPassword}
          />

          <TextField
            error={Boolean(form.confirmPassword && !passwordsMatch)}
            fullWidth
            helperText={
              form.confirmPassword && !passwordsMatch
                ? 'Passwords do not match'
                : ''
            }
            label="Confirm New Password"
            onChange={handleChange('confirmPassword')}
            required
            size="small"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        showConfirmPassword
                          ? 'Hide confirm password'
                          : 'Show confirm password'
                      }
                      edge="end"
                      onClick={() =>
                        setShowConfirmPassword(previous => !previous)
                      }
                      sx={visibilityButtonSx}>
                      {showConfirmPassword ? (
                        <VisibilityOffOutlined sx={{ fontSize: 18 }} />
                      ) : (
                        <VisibilityOutlined sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                )
              }
            }}
            sx={textFieldSx}
            type={showConfirmPassword ? 'text' : 'password'}
            value={form.confirmPassword}
          />
        </Stack>
      </DialogContent>
      <CommonDialogActions
        onCancel={onClose}
        onSubmit={handleSubmit}
        submitText="Change Password"
        disabled={isSubmitDisabled}
      />
    </Dialog>
  );
};

ChangePasswordModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired
};

export default ChangePasswordModal;
